import { useState, useRef, useEffect } from "react";
import { joinValuesWithPipe } from "@utils";

const useHyroListField = ({ values, onChange, onFocus, onBlur, open, showCopyButton }) => {
  const [value, setValue] = useState("");
  const [isFocused, setIsFocused] = useState(open);
  const [anchorEl, setAnchorEl] = useState(null);
  const popperRef = useRef(null);
  const prevValuesLength = useRef(values.length);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isCopyButtonVisible = showCopyButton && values.length > 0;

  useEffect(() => {
    if (values.length === 0) {
      setValue("");
    }
  }, [values]);

  useEffect(() => {
    if (popperRef.current && values.length > prevValuesLength.current) {
      popperRef.current.scrollTop = popperRef.current.scrollHeight;
    }
    prevValuesLength.current = values.length;
  }, [values]);

  const handlePaste = (event) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData("text");
    const pastedValues = pastedText.split("|").map(value => value.trim()).filter(Boolean);
    const uniqueValues = [...new Set([...values, ...pastedValues])];
    if (uniqueValues.length !== values.length) {
      onChange(event, uniqueValues);
    }
  };

  const handleFocus = (event) => {
    if (isMenuOpen) return;
    onFocus?.(event);
    setAnchorEl(event.currentTarget);
    setIsFocused(true);
  };

  const handleAutoCompleteChange = (event, newValue) => {
    const isClearIconClicked = Array.isArray(newValue) && newValue.length === 0;
    if (isClearIconClicked) {
      setValue("");
    } else {
      const allValues = newValue.map(value => value.trim()).filter(Boolean);
      if (JSON.stringify(allValues) === JSON.stringify(values)) {
        return;
      }
      const newVal = [...new Set(allValues)];
      onChange(event, newVal);
      setValue("");
    }
  };

  const handleCopyAll = () => {
    const text = joinValuesWithPipe(values);
    navigator.clipboard.writeText(text);
  };

  const handleClearAll = () => {
    onChange(null, []);
    setValue("");
  };

  const handleBlur = (e) => {
    if (isMenuOpen) return;
    setIsFocused(false);
    setAnchorEl(null);

    const trimmedValue = value.trim();

    if (trimmedValue && !values.includes(trimmedValue)) {
      onChange(e, [...values, trimmedValue]);
      setValue("");
    }

    onBlur?.();
  };

  return {
    value,
    setValue,
    isFocused,
    setIsFocused,
    anchorEl,
    setAnchorEl,
    popperRef,
    prevValuesLength,
    isMenuOpen,
    setIsMenuOpen,
    isCopyButtonVisible,
    handlePaste,
    handleFocus,
    handleAutoCompleteChange,
    handleCopyAll,
    handleClearAll,
    handleBlur
  };
};

export default useHyroListField;
